import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Store,
    Palette,
    UserCircle,
    ChevronRight,
    ChevronLeft,
    Upload,
    CheckCircle2,
    Briefcase,
    Globe,
    Building,
    Coffee,
    Utensils,
    Cake,
    ChefHat,
    LayoutGrid,
    X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { useTranslation } from 'react-i18next';

const SetupWizard = () => {
    const [step, setStep] = useState(1);
    const { signup } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [kvkkAccepted, setKvkkAccepted] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        // Step 1: Business
        companyName: '',
        industry: '',
        website: '',
        address: '',
        businessPhone: '',
        businessPhoneCode: '+90',
        city: '',
        district: '',
        employeeCount: '1-10',
        // Step 2: Identity
        logo: null,
        logoPreview: null,
        banner: null,
        bannerPreview: null,
        // Step 3: Admin
        firstName: '',
        lastName: '',
        position: '',
        email: '',
        phone: '',
        phoneCode: '+90',
        password: '',
        confirmPassword: ''
    });

    const capitalizeWords = (str) => {
        if (!str) return '';
        return str.split(' ').map(word =>
            word.length > 0 ? word.charAt(0).toLocaleUpperCase('tr') + word.slice(1).toLocaleLowerCase('tr') : ''
        ).join(' ');
    };

    const handleChange = (e) => {
        let val = e.target.value;
        const name = e.target.name;

        if (name === 'businessPhone' || name === 'phone') {
            val = val.replace(/\D/g, '');
            if (val.startsWith('0')) val = val.substring(1);
            if (val.length > 10) val = val.substring(0, 10);
        } else if (['companyName', 'city', 'district', 'address', 'firstName', 'lastName', 'position'].includes(name)) {
            val = capitalizeWords(val);
        }
        
        setFormData({ ...formData, [name]: val });
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Basic file type validation
        if (!file.type.startsWith('image/')) {
            showAlert("Lütfen geçerli bir resim dosyası seçin.", "warning");
            return;
        }

        const reader = new FileReader();
        reader.onerror = () => showAlert("Dosya okunurken bir hata oluştu.", "error");
        reader.onload = (event) => {
            const img = new Image();
            img.onerror = () => showAlert("Resim yüklenirken bir hata oluştu. Dosya bozuk olabilir.", "error");
            img.onload = () => {
                setFormData({
                    ...formData,
                    logo: file,
                    logoPreview: event.target.result
                });
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleBannerChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showAlert("Lütfen geçerli bir resim dosyası seçin.", "warning");
            return;
        }

        const reader = new FileReader();
        reader.onerror = () => showAlert("Dosya okunurken bir hata oluştu.", "error");
        reader.onload = (event) => {
            setFormData({
                ...formData,
                banner: file,
                bannerPreview: event.target.result
            });
        };
        reader.readAsDataURL(file);
    };

    const [signupError, setSignupError] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState(['', '', '', '']);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpResendCooldown, setOtpResendCooldown] = useState(0);
    const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
    const cooldownTimerRef = useRef(null);

    // Cooldown timer effect
    useEffect(() => {
        if (otpResendCooldown > 0) {
            cooldownTimerRef.current = setTimeout(() => {
                setOtpResendCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearTimeout(cooldownTimerRef.current);
    }, [otpResendCooldown]);

    // Auto-focus first OTP input when modal opens
    useEffect(() => {
        if (showOtpModal && otpRefs[0].current) {
            setTimeout(() => otpRefs[0].current.focus(), 100);
        }
    }, [showOtpModal]);

    const validatePhoneFormat = (phone, code) => {
        if (code === '+90') {
            const cleaned = phone.replace(/\D/g, '');
            if (cleaned.length !== 10) return 'Telefon numarasi 10 haneli olmalidir (5XX XXX XX XX)';
            if (!cleaned.startsWith('5')) return 'Telefon numarasi 5 ile baslamalidir';
        }
        return null;
    };

    const sendOtp = useCallback(async () => {
        const res = await fetch('/api/send-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email, type: 'business' })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'OTP gonderilemedi');
        setOtpResendCooldown(60);
    }, [formData.email]);

    const handleSubmit = async () => {
        setSignupError('');
        setLoading(true);
        try {
            if (!kvkkAccepted) {
                throw new Error("Devam etmek için KVKK Aydınlatma Metni ve Gizlilik Sözleşmesi'ni onaylamalısınız.");
            }
            if (formData.password !== formData.confirmPassword) {
                throw new Error("Sifreler eslesmiyor");
            }

            // Validate admin phone
            const phoneErr = validatePhoneFormat(formData.phone, formData.phoneCode);
            if (phoneErr) throw new Error(phoneErr);

            // Validate business phone if provided
            if (formData.businessPhone) {
                const bizPhoneErr = validatePhoneFormat(formData.businessPhone, formData.businessPhoneCode);
                if (bizPhoneErr) throw new Error('Isletme telefonu: ' + bizPhoneErr);
            }

            // Send OTP instead of signing up directly
            await sendOtp();
            setOtpCode(['', '', '', '']);
            setOtpError('');
            setShowOtpModal(true);
        } catch (err) {
            setSignupError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        // Only allow digits
        const digit = value.replace(/\D/g, '');
        if (digit.length > 1) return; // single digit only (paste handled separately)

        const newCode = [...otpCode];
        newCode[index] = digit;
        setOtpCode(newCode);
        setOtpError('');

        // Auto-advance to next field
        if (digit && index < 3) {
            otpRefs[index + 1].current?.focus();
        }

        // Auto-verify when all 4 digits entered
        if (digit && index === 3 && newCode.every(d => d !== '')) {
            verifyOtp(newCode.join(''));
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            otpRefs[index - 1].current?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
        if (!pasted) return;

        const newCode = ['', '', '', ''];
        for (let i = 0; i < pasted.length; i++) {
            newCode[i] = pasted[i];
        }
        setOtpCode(newCode);
        setOtpError('');

        // Focus appropriate field
        const focusIndex = Math.min(pasted.length, 3);
        otpRefs[focusIndex].current?.focus();

        // Auto-verify if all 4 digits pasted
        if (pasted.length === 4) {
            verifyOtp(pasted);
        }
    };

    const verifyOtp = async (code) => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const res = await fetch('/api/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email, code })
            });
            const data = await res.json();
            if (!res.ok) {
                if (data.error?.toLowerCase().includes('expire') || data.error?.toLowerCase().includes('suresi')) {
                    setOtpError('Kodun suresi doldu. Lutfen yeni kod gonderin.');
                } else {
                    setOtpError(data.error || 'Dogrulama basarisiz');
                }
                setOtpCode(['', '', '', '']);
                setTimeout(() => otpRefs[0].current?.focus(), 100);
                return;
            }
            // OTP verified - proceed with signup
            await completeSignup();
        } catch (err) {
            setOtpError(err.message || 'Bir hata olustu');
            setOtpCode(['', '', '', '']);
            setTimeout(() => otpRefs[0].current?.focus(), 100);
        } finally {
            setOtpLoading(false);
        }
    };

    const completeSignup = async () => {
        try {
            await signup(formData.email, formData.password, {
                companyName: formData.companyName,
                industry: formData.industry,
                website: formData.website,
                address: formData.address,
                city: formData.city,
                district: formData.district,
                businessPhone: formData.businessPhone ? `${formData.businessPhoneCode}${formData.businessPhone}` : '',
                phone: `${formData.phoneCode}${formData.phone.replace(/\D/g, '')}`,
                employeeCount: formData.employeeCount,
                logo: formData.logo,
                banner: formData.banner,
                adminInfo: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    position: formData.position
                }
            });
            setShowOtpModal(false);
            navigate('/login', { state: { message: 'Hesabiniz basariyla olusturuldu! Giris yapabilirsiniz.' } });
        } catch (err) {
            setOtpError(err.message);
        }
    };

    const handleResendOtp = async () => {
        if (otpResendCooldown > 0) return;
        setOtpLoading(true);
        setOtpError('');
        try {
            await sendOtp();
            setOtpCode(['', '', '', '']);
            setTimeout(() => otpRefs[0].current?.focus(), 100);
        } catch (err) {
            setOtpError(err.message);
        } finally {
            setOtpLoading(false);
        }
    };

    const handleOtpCancel = () => {
        setShowOtpModal(false);
        setOtpCode(['', '', '', '']);
        setOtpError('');
        setOtpLoading(false);
    };

    const nextStep = () => setStep(step + 1);
    const prevStep = () => setStep(step - 1);

    const countries = [
        { name: 'Türkiye', code: '+90', flag: '🇹🇷' },
        { name: 'Azerbaycan', code: '+994', flag: '🇦🇿' },
        { name: 'Kıbrıs', code: '+392', flag: '🇹🇷' },
        { name: 'Rusya', code: '+7', flag: '🇷🇺' },
        { name: 'Almanya', code: '+49', flag: '🇩🇪' },
        { name: 'Fransa', code: '+33', flag: '🇫🇷' },
        { name: 'İngiltere', code: '+44', flag: '🇬🇧' },
        { name: 'İtalya', code: '+39', flag: '🇮🇹' },
        { name: 'İspanya', code: '+34', flag: '🇪🇸' },
        { name: 'Hollanda', code: '+31', flag: '🇳🇱' },
        { name: 'Belçika', code: '+32', flag: '🇧🇪' },
        { name: 'İsviçre', code: '+41', flag: '🇨🇭' },
        { name: 'Avusturya', code: '+43', flag: '🇦🇹' },
        { name: 'Yunanistan', code: '+30', flag: '🇬🇷' },
        { name: 'Bulgaristan', code: '+359', flag: '🇧🇬' },
        { name: 'Gürcistan', code: '+995', flag: '🇬🇪' },
        { name: 'Ukrayna', code: '+380', flag: '🇺🇦' },
        { name: 'ABD / Kanada', code: '+1', flag: '🇺🇸' },
        { name: 'Avustralya', code: '+61', flag: '🇦🇺' },
        { name: 'Çin', code: '+86', flag: '🇨🇳' },
        { name: 'Japonya', code: '+81', flag: '🇯🇵' },
        { name: 'Güney Kore', code: '+82', flag: '🇰🇷' },
        { name: 'Hindistan', code: '+91', flag: '🇮🇳' },
        { name: 'Brezilya', code: '+55', flag: '🇧🇷' },
        { name: 'Arjantin', code: '+54', flag: '🇦🇷' },
        { name: 'Meksika', code: '+52', flag: '🇲🇽' },
        { name: 'Mısır', code: '+20', flag: '🇪🇬' },
        { name: 'BAE', code: '+971', flag: '🇦🇪' },
        { name: 'Suudi Arabistan', code: '+966', flag: '🇸🇦' },
        { name: 'Katar', code: '+974', flag: '🇶🇦' },
        { name: 'Kuveyt', code: '+965', flag: '🇰🇼' },
        { name: 'Irak', code: '+964', flag: '🇮🇶' },
        { name: 'İran', code: '+98', flag: '🇮🇷' },
        { name: 'Kazakistan', code: '+7', flag: '🇰🇿' },
        { name: 'Özbekistan', code: '+998', flag: '🇺🇿' },
        { name: 'Türkmenistan', code: '+993', flag: '🇹🇲' },
        { name: 'Kırgızistan', code: '+996', flag: '🇰🇬' },
        { name: 'Tacikistan', code: '+992', flag: '🇹🇯' },
    ];

    return (
        <>
        {showOtpModal && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '1rem'
            }}>
                <div style={{
                    background: 'var(--bg-card)',
                    borderRadius: '24px',
                    padding: '2.5rem',
                    maxWidth: '420px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    border: '1px solid var(--border)',
                    position: 'relative'
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'rgba(111, 78, 55, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1.5rem'
                    }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    </div>

                    <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem', color: 'var(--text-main)' }}>
                        E-posta Dogrulama
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '2rem' }}>
                        <strong>{formData.email}</strong> adresine 4 haneli dogrulama kodu gonderdik.
                    </p>

                    {/* OTP Input Boxes */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '12px',
                        marginBottom: '1.5rem'
                    }}>
                        {otpCode.map((digit, idx) => (
                            <input
                                key={idx}
                                ref={otpRefs[idx]}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleOtpChange(idx, e.target.value)}
                                onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                onPaste={idx === 0 ? handleOtpPaste : undefined}
                                disabled={otpLoading}
                                style={{
                                    width: '56px',
                                    height: '64px',
                                    textAlign: 'center',
                                    fontSize: '1.75rem',
                                    fontWeight: 700,
                                    borderRadius: '16px',
                                    border: `2px solid ${otpError ? '#ef4444' : digit ? 'var(--primary)' : 'var(--border)'}`,
                                    background: 'var(--bg-sidebar)',
                                    color: 'var(--text-main)',
                                    outline: 'none',
                                    transition: 'all 0.2s',
                                    caretColor: 'var(--primary)',
                                    opacity: otpLoading ? 0.6 : 1
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--primary)';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(111, 78, 55, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = digit ? 'var(--primary)' : 'var(--border)';
                                    e.target.style.boxShadow = 'none';
                                }}
                                aria-label={`Dogrulama kodu ${idx + 1}. hane`}
                            />
                        ))}
                    </div>

                    {otpLoading && (
                        <p style={{ color: 'var(--primary)', fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem' }}>
                            Dogrulaniyor...
                        </p>
                    )}

                    {otpError && (
                        <div style={{
                            background: '#fff1f2',
                            color: '#e11d48',
                            padding: '0.75rem',
                            borderRadius: '10px',
                            marginBottom: '1rem',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            border: '1px solid #ffe4e6'
                        }}>
                            {otpError}
                        </div>
                    )}

                    {/* Resend button with cooldown */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        {otpResendCooldown > 0 ? (
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                                Kodu tekrar gonder ({otpResendCooldown}s)
                            </p>
                        ) : (
                            <button
                                onClick={handleResendOtp}
                                disabled={otpLoading}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--primary)',
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    cursor: otpLoading ? 'not-allowed' : 'pointer',
                                    textDecoration: 'underline',
                                    opacity: otpLoading ? 0.6 : 1
                                }}
                            >
                                Kodu tekrar gonder
                            </button>
                        )}
                    </div>

                    <div style={{ padding: '0.75rem', background: 'rgba(111, 78, 55, 0.05)', borderRadius: '12px', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                        E-posta gelmediyse spam/gereksiz klasorunu kontrol edin.
                    </div>

                    {/* Cancel button */}
                    <button
                        onClick={handleOtpCancel}
                        disabled={otpLoading}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-muted)',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            margin: '0 auto'
                        }}
                    >
                        <ChevronLeft size={16} /> Geri Don
                    </button>
                </div>
            </div>
        )}
        <div className="wizard-page">
            <style>{`
        .wizard-page {
          min-height: 100vh;
          background: var(--bg-main);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 4rem 1rem;
          font-family: var(--font-family);
        }

        .wizard-container {
          width: 100%;
          max-width: 600px;
          background: var(--bg-card);
          border-radius: 24px;
          box-shadow: var(--shadow-md);
          padding: 3rem;
          position: relative;
          overflow: hidden;
          border: 1px solid var(--border);
        }

        .phone-input-group {
          display: flex;
          gap: 10px;
        }

        .phone-code-select {
          width: 120px !important;
          flex-shrink: 0;
        }

        .step-indicator {
          display: flex;
          justify-content: space-between;
          margin-bottom: 3rem;
          position: relative;
        }

        .step-indicator::before {
          content: '';
          position: absolute;
          top: 18px;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--border);
          z-index: 1;
        }

        .step-dot {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--bg-card);
          border: 2px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          color: var(--text-muted);
          transition: all 0.3s;
        }

        .step-dot.active {
          border-color: var(--primary);
          color: var(--primary);
          background: rgba(var(--primary-rgb), 0.1);
        }

        .step-dot.completed {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
        }

        .form-title {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: var(--text-main);
        }

        .form-subtitle {
          color: #64748b;
          margin-bottom: 2.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
          text-align: left;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--text-muted);
        }

        .form-group input, .form-group select {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--border);
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.2s;
          box-sizing: border-box;
          background: var(--bg-sidebar);
          color: var(--text-main);
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(111, 78, 55, 0.1);
        }

        .industry-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .industry-card {
          padding: 1.5rem 1rem;
          border: 1px solid var(--border);
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          text-align: center;
          background: var(--bg-card);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .industry-card:hover {
          border-color: var(--primary);
          background: rgba(var(--primary-rgb), 0.05);
          transform: translateY(-4px);
        }

        .industry-card.active {
          border-color: var(--primary);
          background: rgba(var(--primary-rgb), 0.1);
          border-width: 2px;
          box-shadow: 0 10px 20px -5px rgba(var(--primary-rgb), 0.2);
        }

        .industry-card .icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(var(--primary-rgb), 0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary);
          transition: all 0.3s;
        }

        .industry-card.active .icon-wrapper {
          background: var(--primary);
          color: white;
        }

        .industry-card .label {
          font-size: 0.875rem;
          font-weight: 700;
          color: var(--text-muted);
          transition: all 0.3s;
        }

        .industry-card.active .label {
          color: var(--primary);
        }

        .btn-row {
          display: flex;
          justify-content: space-between;
          margin-top: 3rem;
        }

        .btn-next {
          background: var(--primary);
          color: white;
          padding: 0.75rem 2rem;
          border-radius: 12px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-back {
          background: transparent;
          color: #64748b;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .logo-upload-box {
          border: 2px dashed var(--border);
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .logo-upload-box:hover {
          border-color: var(--primary);
          background: #fdfaf7;
        }

        .preview-circle {
          width: 100px;
          height: 100px;
          border-radius: 20px;
          margin: 0 auto 1rem;
          background: var(--bg-main);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .preview-circle img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .logo-section {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .logo-preview-area {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .brand-demo {
          background: #f8fafc;
          border-radius: 20px;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 1rem;
          border: 1px solid var(--border);
        }

        .demo-bar {
          height: 12px;
          border-radius: 6px;
          width: 80%;
          background: var(--border);
          opacity: 0.5;
        }

        .demo-button {
          height: 36px;
          border-radius: 8px;
          width: 100px;
          transition: background 0.3s;
        }

        .demo-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 0.5rem;
        }

        .demo-logo-mini {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          background: white;
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .demo-logo-mini img {
          width: 80%;
          height: 80%;
          object-fit: contain;
        }
      `}</style>

            <div className="wizard-container">
                <div className="step-indicator">
                    <div className={`step-dot ${step >= 1 ? (step > 1 ? 'completed' : 'active') : ''}`}>
                        {step > 1 ? <CheckCircle2 size={24} /> : <Store size={20} />}
                    </div>
                    <div className={`step-dot ${step >= 2 ? (step > 2 ? 'completed' : 'active') : ''}`}>
                        {step > 2 ? <CheckCircle2 size={24} /> : <Palette size={20} />}
                    </div>
                    <div className={`step-dot ${step === 3 ? 'active' : ''}`}>
                        <UserCircle size={20} />
                    </div>
                </div>

                {step === 1 && (
                    <div className="step-content">
                        <h2 className="form-title">{t('setup.businessProfile')}</h2>
                        <p className="form-subtitle">{t('setup.businessProfileDesc')}</p>

                        <div className="form-group">
                            <label>{t('settings.companyName')}</label>
                            <input
                                name="companyName"
                                autoCapitalize="words"
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="Örn: Ugra Kafe"
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('setup.website')}</label>
                            <input
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://isletmeniz.com"
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('setup.phone')} (Opsiyonel)</label>
                            <div className="phone-input-group">
                                <select
                                    name="businessPhoneCode"
                                    value={formData.businessPhoneCode}
                                    onChange={handleChange}
                                    className="phone-code-select"
                                >
                                    {countries.map(c => (
                                        <option key={c.name} value={c.code}>{c.flag} {c.code}</option>
                                    ))}
                                </select>
                                <input
                                    name="businessPhone"
                                    value={formData.businessPhone}
                                    onChange={handleChange}
                                    placeholder="5XX XXX XX XX"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>İl</label>
                                <input
                                    name="city"
                                    autoCapitalize="words"
                                    value={formData.city}
                                    onChange={handleChange}
                                    placeholder="Örn: İstanbul"
                                />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>İlçe</label>
                                <input
                                    name="district"
                                    autoCapitalize="words"
                                    value={formData.district}
                                    onChange={handleChange}
                                    placeholder="Örn: Beşiktaş"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('setup.address')} (Opsiyonel)</label>
                            <input
                                name="address"
                                autoCapitalize="words"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="İşletmenizdeki tam adresi"
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('setup.industry')}</label>
                            <div className="industry-grid">
                                {[
                                    { id: 'cafe', name: t('setup.industries.cafe'), icon: Coffee },
                                    { id: 'patisserie', name: t('setup.industries.patisserie'), icon: Cake },
                                    { id: 'restaurant', name: t('setup.industries.restaurant'), icon: Utensils },
                                    { id: 'bakery', name: t('setup.industries.bakery'), icon: ChefHat },
                                    { id: 'other', name: t('setup.industries.other'), icon: LayoutGrid }
                                ].map((ind) => (
                                    <div
                                        key={ind.id}
                                        className={`industry-card ${formData.industry === ind.id ? 'active' : ''}`}
                                        onClick={() => setFormData({ ...formData, industry: ind.id })}
                                    >
                                        <div className="icon-wrapper">
                                            <ind.icon size={24} />
                                        </div>
                                        <span className="label">{ind.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="btn-row">
                            <Link to="/" className="btn-back"><ChevronLeft size={18} /> {t('common.cancel')}</Link>
                            <button
                                className="btn-next"
                                onClick={nextStep}
                                disabled={!formData.companyName || !formData.industry}
                            >
                                {t('common.next')} <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="step-content">
                        <h2 className="form-title">{t('setup.corporateIdentity')}</h2>
                        <p className="form-subtitle">{t('setup.corporateIdentityDesc')}</p>

                        <div className="logo-section">
                            <div className="logo-preview-area">
                                {/* Upload Box */}
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="file"
                                        id="logo-input"
                                        hidden
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                    <label htmlFor="logo-input" className="logo-upload-box" style={{
                                        height: '240px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        borderColor: formData.logo ? 'var(--primary)' : 'var(--border)',
                                        background: formData.logo ? 'rgba(var(--primary-rgb), 0.02)' : 'var(--bg-card)'
                                    }}>
                                        <div className="preview-circle" style={{
                                            width: '80px',
                                            height: '80px',
                                            borderColor: 'var(--primary)',
                                            borderStyle: 'solid',
                                            borderWidth: formData.logo ? '2px' : '0px',
                                            boxShadow: formData.logo ? '0 10px 20px -5px rgba(var(--primary-rgb), 0.2)' : 'none'
                                        }}>
                                            {formData.logoPreview ? (
                                                <img src={formData.logoPreview} alt="Preview" />
                                            ) : (
                                                <Upload size={32} color="#94a3b8" />
                                            )}
                                        </div>
                                        <p style={{ fontWeight: 700, color: 'var(--text-main)', fontSize: '0.9375rem', marginBottom: '4px' }}>
                                            {formData.logo ? 'Logo Değiştir' : 'Logo Yükle'}
                                        </p>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            PNG, JPG veya SVG (Önerilen: Kare)
                                        </p>
                                    </label>
                                    {formData.logo && (
                                        <button
                                            onClick={() => setFormData({ ...formData, logo: null, logoPreview: null })}
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                right: '-10px',
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>

                                {/* Banner Upload Box */}
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="file"
                                        id="banner-input"
                                        hidden
                                        accept="image/*"
                                        onChange={handleBannerChange}
                                    />
                                    <label htmlFor="banner-input" className="logo-upload-box" style={{
                                        height: '240px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        borderColor: formData.banner ? 'var(--primary)' : 'var(--border)',
                                        background: formData.banner ? 'rgba(var(--primary-rgb), 0.02)' : 'var(--bg-card)'
                                    }}>
                                        <div className="preview-rectangle" style={{
                                            width: '100%',
                                            height: '80px',
                                            borderRadius: '12px',
                                            marginBottom: '1rem',
                                            background: 'var(--bg-main)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            border: formData.banner ? '2px solid var(--primary)' : '0px solid transparent'
                                        }}>
                                            {formData.bannerPreview ? (
                                                <img src={formData.bannerPreview} alt="Banner Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <LayoutGrid size={32} color="#94a3b8" />
                                            )}
                                        </div>
                                        <p style={{ fontWeight: 700, color: 'var(--text-main)', fontSize: '0.9375rem', marginBottom: '4px' }}>
                                            {formData.banner ? 'Banner Değiştir' : 'Banner Yükle'}
                                        </p>
                                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            PNG, JPG veya SVG (Önerilen: Yatay)
                                        </p>
                                    </label>
                                    {formData.banner && (
                                        <button
                                            onClick={() => setFormData({ ...formData, banner: null, bannerPreview: null })}
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                right: '-10px',
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                background: '#ef4444',
                                                color: 'white',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>

                                {/* Live Appearance Preview */}
                                <div className="brand-demo" style={{ gridColumn: 'span 2' }}>
                                    <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                                        İşletme Kartı Önizleme
                                    </p>
                                    <div style={{ background: 'white', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', maxWidth: '300px' }}>
                                        <div style={{ height: '80px', background: formData.bannerPreview ? `url(${formData.bannerPreview}) center/cover` : '#f1f5f9', position: 'relative' }}>
                                            <div style={{ position: 'absolute', bottom: '-20px', left: '16px', width: '48px', height: '48px', borderRadius: '12px', background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                {formData.logoPreview ? <img src={formData.logoPreview} style={{ width: '80%', height: '80%', objectFit: 'contain' }} alt="" /> : <Coffee size={20} color="var(--primary)" />}
                                            </div>
                                        </div>
                                        <div style={{ padding: '28px 16px 16px' }}>
                                            <div style={{ height: '14px', width: '120px', borderRadius: '7px', background: 'var(--primary)', marginBottom: '8px' }}></div>
                                            <div style={{ height: '10px', width: '80px', borderRadius: '5px', background: 'var(--border)' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                        </div>

                        <div className="btn-row">
                            <button className="btn-back" onClick={prevStep}><ChevronLeft size={18} /> {t('common.back')}</button>
                            <button className="btn-next" onClick={nextStep} style={{ background: 'var(--primary)' }}>
                                {t('common.next')} <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="step-content">
                        <h2 className="form-title">{t('setup.adminAccount')}</h2>
                        <p className="form-subtitle">{t('setup.adminAccountDesc')}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className="form-group">
                                <label>{t('common.firstName')}</label>
                                <input
                                    name="firstName"
                                    autoComplete="given-name"
                                    autoCapitalize="words"
                                    placeholder="Adınız"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('common.lastName')}</label>
                                <input
                                    name="lastName"
                                    autoComplete="family-name"
                                    autoCapitalize="words"
                                    placeholder="Soyadınız"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{t('setup.position')}</label>
                            <input
                                name="position"
                                autoCapitalize="words"
                                value={formData.position}
                                onChange={handleChange}
                                placeholder="Örn: İşletme Sahibi, Müdür"
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('common.email')}</label>
                            <input
                                name="email"
                                autoComplete="email"
                                typeof="email"
                                value={formData.email} 
                                onChange={handleChange} 
                                placeholder="isim@isletme.com" 
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('setup.phone')}</label>
                            <div className="phone-input-group">
                                <select
                                    name="phoneCode"
                                    value={formData.phoneCode}
                                    onChange={handleChange}
                                    className="phone-code-select"
                                >
                                    {countries.map(c => (
                                        <option key={c.name} value={c.code}>{c.flag} {c.code}</option>
                                    ))}
                                </select>
                                <input
                                    name="phone"
                                    autoComplete="tel-national"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="5XX XXX XX XX"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className="form-group">
                                <label>{t('login.password')}</label>
                                <input 
                                    name="password" 
                                    type="password" 
                                    autoComplete="new-password"
                                    value={formData.password} 
                                    onChange={handleChange} 
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('login.confirmPassword', 'Confirm Password')}</label>
                                <input 
                                    name="confirmPassword" 
                                    type="password" 
                                    autoComplete="new-password"
                                    value={formData.confirmPassword} 
                                    onChange={handleChange} 
                                />
                            </div>
                        </div>

                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '1.5rem', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            <input
                                type="checkbox"
                                checked={kvkkAccepted}
                                onChange={e => setKvkkAccepted(e.target.checked)}
                                style={{ width: '18px', height: '18px', marginTop: '1px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary)' }}
                            />
                            <span>
                                <Link to="/kvkk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>KVKK Aydınlatma Metni</Link>
                                {' '}ve{' '}
                                <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>Gizlilik Sözleşmesi</Link>
                                {"'ni okudum, kişisel verilerimin işlenmesini kabul ediyorum."}
                            </span>
                        </label>

                        <div className="btn-row">
                            <button className="btn-back" onClick={prevStep}><ChevronLeft size={18} /> {t('common.back')}</button>
                            <button
                                className="btn-next"
                                onClick={handleSubmit}
                                disabled={loading || !formData.email || !formData.phone || !formData.password || formData.password !== formData.confirmPassword || !kvkkAccepted}
                                style={{ background: 'var(--primary)' }}
                            >
                                {loading ? t('common.loading') : t('setup.finalize')} <CheckCircle2 size={18} />
                            </button>
                        </div>
                        {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                            <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '1rem', textAlign: 'center' }}>
                                {t('setup.passwordMismatch')}
                            </p>
                        )}
                        {signupError && (
                            <div style={{ background: '#fff1f2', color: '#e11d48', padding: '0.75rem', borderRadius: '10px', marginTop: '1rem', fontSize: '0.8125rem', fontWeight: 500, textAlign: 'center', border: '1px solid #ffe4e6' }}>
                                {signupError}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default SetupWizard;
